import { apiClient } from '@/services/apiClient';
import { presignLargeFiles } from '@/services/uploads.service';
import type { ApiResponse, PaginationMeta } from '@/types/api.types';

export type SupportTicketStatus = 'open' | 'in_progress' | 'observation' | 'on_hold' | 'pending_customer' | 'resolved' | 'closed';
export type SupportTicketPriority = 'critical' | 'high' | 'medium' | 'low';
export type SupportTicketType = 'bug' | 'account' | 'billing' | 'feature_request' | 'legacy_issue' | 'other';

export interface SupportTicketAssignee {
  userId: number;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  isSuperAdmin: boolean;
  isAdmin: boolean;
}

/**
 * Labels assignees by name (or email when name is empty). When two master
 * users share the same display name, the email is appended to disambiguate.
 */
export function buildSupportTicketAssigneeSelectOptions(assignees: SupportTicketAssignee[]): { value: number; label: string }[] {
  const nameKey = (a: SupportTicketAssignee) => (a.displayName || a.email || '').toLowerCase();
  const keyCounts = new Map<string, number>();
  for (const a of assignees) {
    const k = nameKey(a);
    keyCounts.set(k, (keyCounts.get(k) ?? 0) + 1);
  }
  return assignees.map((assignee) => {
    const base = assignee.displayName || assignee.email;
    const dup = (keyCounts.get(nameKey(assignee)) ?? 0) > 1;
    return {
      value: assignee.userId,
      label: dup && assignee.email ? `${base} <${assignee.email}>` : base,
    };
  });
}

export interface SupportTicketAttachment {
  supportTicketAttachmentId: number;
  supportTicketId: number;
  supportTicketMessageId: number | null;
  originalFilename: string;
  storedFilename: string;
  mimeType: string | null;
  fileSize: number;
  downloadUrl: string;
  createdAt: string | null;
}

export interface SupportTicketMessage {
  supportTicketMessageId: number;
  supportTicketId: number;
  accountId: number;
  authorUserId: number | null;
  authorName: string;
  authorEmail: string | null;
  authorRole: 'requester' | 'support';
  body: string;
  attachmentCount: number;
  attachments: SupportTicketAttachment[];
  isInternal: boolean;
  targetUserId: number | null;
  targetName: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface SupportTicket {
  supportTicketId: number;
  accountId: number;
  accountName: string;
  ticketNumber: string;
  subject: string;
  type: SupportTicketType;
  priority: SupportTicketPriority;
  status: SupportTicketStatus;
  requesterUserId: number | null;
  requesterName: string;
  requesterEmail: string | null;
  assignedToUserId: number | null;
  assignedTo: {
    userId: number;
    displayName: string;
    email: string;
    isSuperAdmin: boolean;
  } | null;
  requesterUnreadCount: number;
  supportUnreadCount: number;
  lastMessageAt: string | null;
  lastMessageByRole: 'requester' | 'support' | null;
  closedAt: string | null;
  mergedIntoSupportTicketId: number | null;
  mergedIntoTicketNumber: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  /**
   * Only populated on the ticket-detail (show) response. `viewerIsAssignee`
   * gates the assignee-only "Prospera Insights" button; `aiInvestigationAvailable`
   * reflects the server-side feature flag. Both default false on list rows.
   * `prosperaMessagingAvailable` gates the "Prospera" internal-note recipient —
   * any manager while the feature is on, regardless of assignee / latest message.
   */
  viewerIsAssignee?: boolean;
  aiInvestigationAvailable?: boolean;
  prosperaMessagingAvailable?: boolean;
  /**
   * The viewer's own unread count — the owner's counter, a follower's own
   * counter, or the support-side counter for managers. On list rows and the
   * detail response.
   */
  viewerUnreadCount?: number;
  /** Detail response only: the people on the ticket and what the viewer may do. */
  owner?: SupportTicketOwner;
  participants?: SupportTicketParticipant[];
  viewerRole?: SupportTicketViewerRole | null;
  /** The viewer's own practice user id; null for support. */
  viewerUserId?: number | null;
  viewerCan?: SupportTicketPeoplePermissions;
  messages: SupportTicketMessage[];
}

export type SupportTicketViewerRole = 'owner' | 'follower' | 'support';

export interface SupportTicketOwner {
  userId: number | null;
  name: string;
  email: string | null;
  isActive: boolean;
}

export interface SupportTicketParticipant {
  userId: number;
  name: string;
  email: string | null;
  isActive: boolean;
  addedByName: string | null;
  addedAt: string | null;
}

/** Computed by the server from the ticket's people rules; the UI only reads it. */
export interface SupportTicketPeoplePermissions {
  addPeople: boolean;
  removeOthers: boolean;
  makeOwner: boolean;
  /** A follower taking over a ticket whose owner's account is inactive. */
  claimOwnership: boolean;
}

export interface SupportTicketParticipantCandidate {
  userId: number;
  name: string;
  email: string | null;
}

/** Options for a people picker: the name, with the email underneath. */
export function buildSupportTicketParticipantOptions(
  candidates: SupportTicketParticipantCandidate[],
): { value: string; label: string; subtitle?: string }[] {
  return candidates.map((candidate) => ({
    value: String(candidate.userId),
    label: candidate.name,
    subtitle: candidate.email ?? undefined,
  }));
}

/**
 * Synthetic internal-note recipient id meaning "address Prospera" (the support
 * assistant) instead of a human. MUST match the backend
 * `config('ai_investigation.prospera_recipient_id')` default (-1).
 */
export const PROSPERA_RECIPIENT_ID = -1;

export type SupportTicketInvestigationStatus = 'pending' | 'ready' | 'failed';

export interface SupportTicketInvestigation {
  aiInvestigationId: number;
  status: SupportTicketInvestigationStatus;
  /** Live progress label shown while status is 'pending' (e.g. "Searching the codebase…"). */
  stage: string | null;
  event: string;
  resultNoteId: number | null;
  error: string | null;
  createdAt: string | null;
  completedAt: string | null;
}

export interface SupportTicketSummary {
  total: number;
  open: number;
  inProgress: number;
  observation: number;
  onHold: number;
  pendingCustomer: number;
  resolved: number;
  closed: number;
  unread: number;
}

export interface SupportTicketListParams {
  page?: number;
  perPage?: number;
  search?: string;
  status?: SupportTicketStatus | string;
  priority?: SupportTicketPriority | string;
  type?: SupportTicketType | string;
  /**
   * Single user id, the literal `unassigned`, or a comma-separated list mixing
   * the two (e.g. `unassigned,12,34`). The API treats it as an OR-set so
   * multi-select in the sidebar surfaces every matching ticket.
   */
  assignedToUserId?: number | string;
  accountID?: number;
  unreadOnly?: boolean;
  /** Omitted = latest activity first. */
  sortBy?: 'activity' | 'opened' | 'priority';
  sortDir?: 'asc' | 'desc';
  /** `Y-m-d`: only tickets opened on or after this day. */
  createdFrom?: string;
}

export interface SupportTicketSummaryParams {
  search?: string;
  priority?: SupportTicketPriority | string;
  type?: SupportTicketType | string;
  assignedToUserId?: number | string;
  accountID?: number;
  unreadOnly?: boolean;
}

export interface CreateSupportTicketPayload {
  subject: string;
  message: string;
  type?: SupportTicketType;
  priority?: SupportTicketPriority;
  attachments?: File[];
  accountID?: number;
  /** Colleagues to add to the ticket as it is created. */
  participantUserIds?: number[];
}

export interface ReplyToSupportTicketPayload {
  message: string;
  attachments?: File[];
}

export interface PostInternalNotePayload {
  message: string;
  targetUserId: number;
  attachments?: File[];
}

export interface UpdateSupportTicketPayload {
  status?: SupportTicketStatus;
  priority?: SupportTicketPriority;
  type?: SupportTicketType;
  assignedToUserId?: number | null;
  /** false skips the requester's status-change email. Omitted means notify. */
  notifyRequester?: boolean;
}

export interface MergeSupportTicketPayload {
  targetSupportTicketId: number;
  reason?: string;
}

export interface SupportTicketListResult {
  data: SupportTicket[];
  meta: PaginationMeta;
}

// Attachments > 4 MB are uploaded directly to S3 and passed as s3Keys[] to
// bypass Lambda's 6 MB payload cap; smaller files ride along as attachments[].
async function appendFiles(formData: FormData, files?: File[]) {
  const { multipartFiles, s3Keys } = await presignLargeFiles(files ?? []);
  multipartFiles.forEach((file) => formData.append('attachments[]', file));
  s3Keys.forEach((key) => formData.append('s3Keys[]', key));
}

async function createTicketFormData(payload: CreateSupportTicketPayload): Promise<FormData> {
  const formData = new FormData();
  formData.append('subject', payload.subject);
  formData.append('message', payload.message);
  if (payload.type) formData.append('type', payload.type);
  if (payload.priority) formData.append('priority', payload.priority);
  if (payload.accountID) formData.append('accountID', String(payload.accountID));
  (payload.participantUserIds ?? []).forEach((userId) => formData.append('participantUserIds[]', String(userId)));
  await appendFiles(formData, payload.attachments);
  return formData;
}

async function createReplyFormData(payload: ReplyToSupportTicketPayload): Promise<FormData> {
  const formData = new FormData();
  formData.append('message', payload.message);
  await appendFiles(formData, payload.attachments);
  return formData;
}

async function createInternalNoteFormData(payload: PostInternalNotePayload): Promise<FormData> {
  const formData = new FormData();
  formData.append('message', payload.message);
  formData.append('targetUserId', String(payload.targetUserId));
  await appendFiles(formData, payload.attachments);
  return formData;
}

function extractDownloadFilename(contentDispositionHeader: string | undefined, fallback: string): string {
  if (!contentDispositionHeader) {
    return fallback;
  }

  const utf8Match = contentDispositionHeader.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const quotedMatch = contentDispositionHeader.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }

  const plainMatch = contentDispositionHeader.match(/filename=([^;]+)/i);
  if (plainMatch?.[1]) {
    return plainMatch[1].trim();
  }

  return fallback;
}

export async function listSupportTickets(params?: SupportTicketListParams): Promise<SupportTicketListResult> {
  const response = await apiClient.get<ApiResponse<SupportTicket[]>>('/support-tickets', { params });
  return {
    data: response.data.data ?? [],
    meta: response.data.meta ?? {},
  };
}

export async function getSupportTicketSummary(params?: SupportTicketSummaryParams): Promise<SupportTicketSummary> {
  const response = await apiClient.get<ApiResponse<SupportTicketSummary>>('/support-tickets/summary', { params });
  return response.data.data;
}

export async function getSupportTicket(supportTicketId: number): Promise<SupportTicket> {
  const response = await apiClient.get<ApiResponse<SupportTicket>>(`/support-tickets/${supportTicketId}`);
  return response.data.data;
}

export async function createSupportTicket(payload: CreateSupportTicketPayload): Promise<SupportTicket> {
  const response = await apiClient.post<ApiResponse<SupportTicket>>(
    '/support-tickets',
    await createTicketFormData(payload),
  );
  return response.data.data;
}

export async function replyToSupportTicket(
  supportTicketId: number,
  payload: ReplyToSupportTicketPayload,
): Promise<{ ticket: SupportTicket; message: SupportTicketMessage }> {
  const response = await apiClient.post<ApiResponse<{ ticket: SupportTicket; message: SupportTicketMessage }>>(
    `/support-tickets/${supportTicketId}/messages`,
    await createReplyFormData(payload),
  );
  return response.data.data;
}

/**
 * Posts an internal note tied to a specific recipient. The note is invisible
 * to the requester (and to any non-manager) and does not affect any of the
 * thread's read counters or last-message metadata, so the practice-facing
 * Help > Tickets view is unchanged by it.
 */
export async function postSupportTicketInternalNote(
  supportTicketId: number,
  payload: PostInternalNotePayload,
): Promise<{
  ticket: SupportTicket;
  message: SupportTicketMessage;
  // Present only when the note was addressed to Prospera: the run kicked off to
  // answer it, so the UI can start polling live progress immediately.
  investigation?: SupportTicketInvestigation | null;
}> {
  const response = await apiClient.post<
    ApiResponse<{
      ticket: SupportTicket;
      message: SupportTicketMessage;
      investigation?: SupportTicketInvestigation | null;
    }>
  >(`/support-tickets/${supportTicketId}/internal-notes`, await createInternalNoteFormData(payload));
  return response.data.data;
}

export async function updateSupportTicket(
  supportTicketId: number,
  payload: UpdateSupportTicketPayload,
): Promise<SupportTicket> {
  const response = await apiClient.patch<ApiResponse<SupportTicket>>(
    `/support-tickets/${supportTicketId}`,
    payload,
  );
  return response.data.data;
}

/**
 * Merges a source ticket into an existing target ticket of the same account.
 * Returns the target ticket payload with the freshly migrated messages and
 * the system "merged from" message appended. The source ticket becomes
 * read-only on the API side after this call.
 */
export async function mergeSupportTicket(
  sourceSupportTicketId: number,
  payload: MergeSupportTicketPayload,
): Promise<SupportTicket> {
  const response = await apiClient.post<ApiResponse<SupportTicket>>(
    `/support-tickets/${sourceSupportTicketId}/merge`,
    payload,
  );
  return response.data.data;
}

/**
 * People who could be added to a ticket. Pass the ticket id for an existing
 * ticket; leave it out on the new-ticket form (a manager then passes accountID).
 */
export async function listSupportTicketParticipantCandidates(params: {
  supportTicketId?: number;
  accountID?: number;
  search?: string;
}): Promise<SupportTicketParticipantCandidate[]> {
  const query: Record<string, string | number> = {};
  if (params.supportTicketId) query.supportTicketId = params.supportTicketId;
  if (params.accountID && params.accountID > 0) query.accountID = params.accountID;
  if (params.search) query.search = params.search;
  const response = await apiClient.get<ApiResponse<SupportTicketParticipantCandidate[]>>(
    '/support-tickets/participant-candidates',
    { params: Object.keys(query).length > 0 ? query : undefined },
  );
  return response.data.data ?? [];
}

export async function addSupportTicketParticipants(supportTicketId: number, userIds: number[]): Promise<SupportTicket> {
  const response = await apiClient.post<ApiResponse<SupportTicket>>(
    `/support-tickets/${supportTicketId}/participants`,
    { userIds },
  );
  return response.data.data;
}

export async function removeSupportTicketParticipant(
  supportTicketId: number,
  userId: number,
): Promise<{ removedUserId: number; viewerLeft: boolean }> {
  const response = await apiClient.delete<ApiResponse<{ removedUserId: number; viewerLeft: boolean }>>(
    `/support-tickets/${supportTicketId}/participants/${userId}`,
  );
  return response.data.data;
}

/** Makes a person already on the ticket its owner; the previous owner stays on. */
export async function transferSupportTicketOwner(supportTicketId: number, userId: number): Promise<SupportTicket> {
  const response = await apiClient.post<ApiResponse<SupportTicket>>(
    `/support-tickets/${supportTicketId}/owner`,
    { userId },
  );
  return response.data.data;
}

export interface SuggestSupportTicketRepliesResponse {
  suggestions: string[];
  kbUsed: boolean;
  sources: Array<{ title: string; url?: string }>;
}

export async function suggestSupportTicketReplies(
  supportTicketId: number,
  status: SupportTicketStatus,
): Promise<SuggestSupportTicketRepliesResponse> {
  const response = await apiClient.post<ApiResponse<SuggestSupportTicketRepliesResponse>>(
    `/support-tickets/${supportTicketId}/suggest-replies`,
    { status },
  );
  return response.data.data;
}

/**
 * Manually starts an AI investigation for a ticket (assignee-only, enforced
 * server-side). Returns the run record; poll getSupportTicketInvestigation for
 * live progress. If a run is already in flight the existing one is returned.
 */
export async function investigateSupportTicket(
  supportTicketId: number,
): Promise<SupportTicketInvestigation> {
  const response = await apiClient.post<ApiResponse<SupportTicketInvestigation>>(
    `/support-tickets/${supportTicketId}/investigate`,
    {},
  );
  return response.data.data;
}

/** Latest investigation run for a ticket (or null if none has ever run). */
export async function getSupportTicketInvestigation(
  supportTicketId: number,
): Promise<SupportTicketInvestigation | null> {
  const response = await apiClient.get<ApiResponse<SupportTicketInvestigation | null>>(
    `/support-tickets/${supportTicketId}/investigation`,
  );
  return response.data.data ?? null;
}

export async function listSupportTicketAssignees(
  search?: string,
  accountID?: number,
): Promise<SupportTicketAssignee[]> {
  const params: Record<string, string | number> = {};
  if (search) params.search = search;
  if (accountID && accountID > 0) params.accountID = accountID;
  const response = await apiClient.get<ApiResponse<SupportTicketAssignee[]>>('/support-tickets/assignable-users', {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  return response.data.data ?? [];
}

export interface SupportTicketAccount {
  accountId: number;
  name: string;
}

export async function listSupportTicketAccounts(): Promise<SupportTicketAccount[]> {
  const response = await apiClient.get<ApiResponse<SupportTicketAccount[]>>('/support-tickets/accounts');
  return response.data.data ?? [];
}

/**
 * Labels accounts as "Name (#id)" so the dropdown matches the format used
 * throughout the admin shell when surfacing accounts.
 */
export function buildSupportTicketAccountSelectOptions(accounts: SupportTicketAccount[]): { value: string; label: string }[] {
  return accounts.map((account) => ({
    value: String(account.accountId),
    label: account.name ? `${account.name} (#${account.accountId})` : `#${account.accountId}`,
  }));
}

/**
 * True when an attachment is an image we can preview inline. Falls back to
 * the original filename's extension when `mimeType` is missing because some
 * older uploads predate the column being populated.
 */
export function isSupportTicketImageAttachment(attachment: SupportTicketAttachment): boolean {
  const mime = (attachment.mimeType ?? '').toLowerCase();
  if (mime.startsWith('image/')) return true;
  if (mime !== '') return false;
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(attachment.originalFilename);
}

/**
 * Fetches an attachment through the authenticated download endpoint and
 * exposes it as an object URL. The browser cannot reach the endpoint with an
 * `<img src>` tag directly because the request needs a bearer token, so we
 * pull it as a blob first.
 *
 * The returned URL is owned by the caller (typically a React Query cache
 * entry) and should not be revoked while the component is still mounted.
 */
export async function fetchSupportTicketAttachmentBlobUrl(
  attachment: SupportTicketAttachment,
): Promise<string | null> {
  const response = await apiClient.get<Blob>(
    `/support-tickets/${attachment.supportTicketId}/attachments/${attachment.supportTicketAttachmentId}/download`,
    { responseType: 'blob' },
  );

  if (!(response.data instanceof Blob) || response.data.size === 0) return null;
  return URL.createObjectURL(response.data);
}

export async function downloadSupportTicketAttachment(attachment: SupportTicketAttachment): Promise<void> {
  const response = await apiClient.get<Blob>(
    `/support-tickets/${attachment.supportTicketId}/attachments/${attachment.supportTicketAttachmentId}/download`,
    { responseType: 'blob' },
  );

  // Surface useful errors instead of "createObjectURL Overload resolution failed".
  // The backend may have returned JSON (e.g. file missing on S3) even though we
  // asked for a blob — peek inside to give the caller a real reason.
  if (!(response.data instanceof Blob)) {
    throw new Error('Server did not return a file (got ' + (typeof response.data) + ').');
  }
  if (response.data.size === 0) {
    throw new Error('File is empty or missing on the server.');
  }
  // If the server returned a JSON error body with a 200 (Lambda streaming
  // quirks), Content-Type will be application/json — try to read it.
  const contentType = (response.headers['content-type'] || '').toLowerCase();
  if (contentType.includes('application/json')) {
    const text = await response.data.text();
    throw new Error('Server returned JSON instead of file: ' + text.slice(0, 200));
  }

  const blobUrl = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  const contentDisposition = response.headers['content-disposition'];

  link.href = blobUrl;
  link.download = extractDownloadFilename(contentDisposition, attachment.originalFilename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}
